import { useCallback, useEffect } from "react";

import { useNavigate } from "react-router-dom";

import { useAtom } from "jotai";

import { atomAccountAddress } from "../atoms/atomsAccount";

import { openDatabase } from "../Utils/db";

import { type IUserDB, IUserLang } from "../interfaces/User";

import authService from "../services/authService";
import { storedLocalApiPort } from "../lib/localApi";

interface DesktopSession {
  accessKey: string | null;

  userId: number | null;

  isAuthenticated: boolean;
}

/**
 * Listens for deep link auth events from the Electron main process.
 * When the user clicks "Open Eriol" on the site, the deep link
 * `eriol-integrations://auth?accessKey=...&userId=...` is received
 * by the main process and forwarded as `app-auth-success` IPC event.
 *
 * Also handles `app-auth-2fa-required` when Google OAuth returns
 * with requiresTwoFactor=true.
 *
 * This hook should be mounted at the app layout level so it's always active.
 */

export const useDeepLinkAuth = () => {
  const navigate = useNavigate();

  const [, setAccount] = useAtom(atomAccountAddress);

  const persistSession = useCallback(
    (accessKey: string, userId: string | number) => {
      const normalizedUserId = String(userId);

      localStorage.setItem("@Eriol/system/accessKey", accessKey);

      localStorage.setItem("@Eriol/system/userId", normalizedUserId);

      setAccount(normalizedUserId);

      window.dispatchEvent(new Event("auth-changed"));

      return normalizedUserId;
    },

    [setAccount],
  );

  const clearSession = useCallback(() => {
    localStorage.removeItem("@Eriol/system/accessKey");

    localStorage.removeItem("@Eriol/system/userId");

    localStorage.removeItem("@Eriol/currentWorkspaceId");

    setAccount(null);

    window.dispatchEvent(new Event("auth-changed"));
  }, [setAccount]);

  const syncSessionWithExtension = useCallback(
    async (userId: string, accessKey: string) => {
      const port = storedLocalApiPort();

      if (port) {
        try {
          await fetch(`http://localhost:${port}/api/session/account`, {
            method: "POST",

            headers: { "Content-Type": "application/json" },

            body: JSON.stringify({ account: userId, accessKey }),
          });
        } catch (err) {
          console.warn("Could not notify extension session:", err);
        }
      }
    },

    [],
  );

  const hydrateSessionFromDesktop = useCallback(async () => {
    const getSession = window.electronAPI?.getSession;

    if (!getSession) return;

    try {
      const session = (await getSession()) as DesktopSession;

      if (session?.isAuthenticated && session.accessKey && session.userId) {
        const currentUserId = localStorage.getItem("@Eriol/system/userId");

        const currentAccessKey = localStorage.getItem(
          "@Eriol/system/accessKey",
        );

        const normalizedUserId = String(session.userId);

        if (
          currentUserId !== normalizedUserId ||
          currentAccessKey !== session.accessKey
        ) {
          const userId = persistSession(session.accessKey, session.userId);

          await syncSessionWithExtension(userId, session.accessKey);

          // First-launch / fresh-session path: also upgrade so the WebView
          // gets cookies. Idempotent + rate-limited backend-side.
          try {
            await authService.upgradeSession();
          } catch {
            /* keep header */
          }
        }

        return;
      }

      // Si en desktop no hay sesión activa, limpiar estado local en renderer.

      if (
        localStorage.getItem("@Eriol/system/userId") ||
        localStorage.getItem("@Eriol/system/accessKey")
      ) {
        clearSession();
      }
    } catch (err) {
      console.warn("Could not hydrate desktop session:", err);
    }
  }, [clearSession, persistSession, syncSessionWithExtension]);

  const handleAppAuth = useCallback(
    async (data: unknown) => {
      const { accessKey, userId } = data as {
        accessKey: string;

        userId: string | number;
      };

      if (!accessKey || !userId) return;

      const normalizedUserId = persistSession(accessKey, userId);

      await syncSessionWithExtension(normalizedUserId, accessKey);

      // Trade the just-stored accessKey for HttpOnly session cookies in
      // the Electron WebView. Subsequent calls authenticate via cookie,
      // unifying us with the browser model. Best-effort — if the upgrade
      // 404s (older backend) we keep the Authorization header fallback.
      try {
        await authService.upgradeSession();
      } catch {
        /* keep Authorization header path */
      }

      // Ensure user exists in local DB

      try {
        const db = await openDatabase();

        const existingUser = await db.user

          .where("account")

          .equals(normalizedUserId)

          .first();

        if (!existingUser) {
          await db.user.add({
            id: Number(normalizedUserId),

            account: normalizedUserId,

            email: "",

            lang: IUserLang.EN,
          } as IUserDB);
        }
      } catch (err) {
        console.warn("Could not sync local DB:", err);
      }

      // Check 2FA recommendation before navigating

      try {
        const user = await authService.getMe();

        if (!user.twoFactorEnabled) {
          navigate("/recommend-2fa");

          return;
        }
      } catch {
        // If getMe fails, continue to install
      }

      navigate("/install");
    },

    [navigate, persistSession, syncSessionWithExtension],
  );

  const handleAuth2FA = useCallback(
    (data: unknown) => {
      const { pendingToken } = data as { pendingToken: string };

      if (!pendingToken) return;

      navigate("/", { state: { googlePendingToken: pendingToken } });
    },

    [navigate],
  );

  const handleAuthLogout = useCallback(() => {
    clearSession();

    navigate("/");
  }, [clearSession, navigate]);

  // Session expiry is now handled by useSessionValidator + SessionExpiredModal.

  // The auth:session-expired event (dispatched by api.ts on 401) is picked up

  // by useSessionValidator which shows the modal instead of auto-redirecting.

  useEffect(() => {
    hydrateSessionFromDesktop();

    window.electronAPI?.on("app-auth-success", handleAppAuth);

    window.electronAPI?.on("app-auth-2fa-required", handleAuth2FA);

    window.electronAPI?.on("app-auth-logout", handleAuthLogout);

    return () => {
      window.electronAPI?.removeListener("app-auth-success");

      window.electronAPI?.removeListener("app-auth-2fa-required");

      window.electronAPI?.removeListener("app-auth-logout");
    };
  }, [
    handleAppAuth,

    handleAuth2FA,

    handleAuthLogout,

    hydrateSessionFromDesktop,
  ]);
};
